import { cva, type VariantProps } from 'class-variance-authority'
import { TextArea as AriaTextArea, type TextAreaProps as AriaTextAreaProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Textarea}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm, md (default), lg
 * - Fluid width by default (`w-full`)
 * - Min-height enforced to prevent single-line collapse
 * - Focus ring uses the brand primary color
 *
 * @example
 * ```tsx
 * const classes = textareaVariants({ size: 'md' })
 * // Returns: "w-full rounded-ds-md border ... min-h-[80px] ..."
 * ```
 */
export const textareaVariants = cva(
	'w-full rounded-ds-md border border-ds-border bg-ds-background px-3 py-2 text-ds-base text-ds-text transition-colors placeholder:text-ds-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50 resize-y',
	{
		variants: {
			size: {
				sm: 'text-ds-sm px-2 py-1.5 min-h-[60px]',
				md: 'text-ds-base px-3 py-2 min-h-[80px]',
				lg: 'text-ds-lg px-4 py-3 min-h-[100px]',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Textarea} component.
 *
 * Extends React Aria's TextAreaProps with CVA size variants.
 *
 * @property size - Textarea size: 'sm' | 'md' (default) | 'lg'
 * @property className - Optional additional Tailwind classes
 * @property placeholder - Placeholder text
 * @property disabled - Disabled state
 * @property readOnly - Read-only state
 * @property required - Required field indicator
 *
 * @remarks
 * React Aria's `TextArea` is the raw multiline input, not a `TextField`: it takes
 * the DOM props (`disabled`, `readOnly`, `required`), NOT the `is`-prefixed ones
 * the wrapped field components take.
 *
 * @example
 * ```tsx
 * <Textarea
 *   size="md"
 *   placeholder="Enter your comments"
 *   required
 * />
 * ```
 */
export interface TextareaProps extends Omit<AriaTextAreaProps, 'className'>, VariantProps<typeof textareaVariants> {
	className?: string
}

/**
 * Accessible multiline text input primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation, focus management, and ARIA attributes
 *   provided by React Aria's `<TextArea>` primitive.
 * - **Styling:** All colors, spacing, and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Sizes:** Small (min-h-60px), medium (min-h-80px, default), large (min-h-100px).
 * - **Fluid width:** Uses `w-full` by default to fill container.
 * - **Resizable:** Vertical resize enabled by default (`resize-y`).
 * - **Focus indicator:** 2px ring in the brand primary color.
 *
 * @example Basic usage
 * ```tsx
 * import { Textarea } from '@atlas/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <form>
 *       <Textarea
 *         placeholder="Enter your message"
 *         isRequired
 *       />
 *     </form>
 *   )
 * }
 * ```
 *
 * @example With controlled value
 * ```tsx
 * const [value, setValue] = useState('')
 *
 * <Textarea
 *   value={value}
 *   onChange={setValue}
 *   placeholder="Type here..."
 *   size="lg"
 * />
 * ```
 *
 * @see {@link textareaVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/textarea/textarea.stories.tsx for visual examples
 */
export function Textarea({ size, className, ...props }: TextareaProps) {
	return <AriaTextArea {...props} className={cn(textareaVariants({ size }), className)} />
}
